<template>
  <DatatableReportV3
    v-if="config"
    id="invoice_exports_report"
    ref="datatable"
    :source="source"
    :config="config"
    :filters="filters"
    subpage
    hide-global-filters
    disable-filter-param-update
    no-download
    bulk-select
    bulk-select-across-pages
    bulk-select-keep-alive
    bulk-select-key="checkbox_row_value"
    :bulk-select-ids="selectedInvoiceIds"
    :map-row-class="handleMapRowClass"
    @draw-complete="$emit('draw-complete', $event)"
    @update:bulk-select-ids="handleUpdateBulkSelectIds"
    @after-init="selectAll"
  />
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import AccountingClassStatus from "@/Reports/AccountingClassStatus.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import UnbatchedExportStatus from "@/Reports/UnbatchedExportStatus.vue";
import store from "@/vuex/store";

export default {
  components: {
    DatatableReportV3
  },
  provide() {
    return {
      datatableAfterInit: this.selectAll
    };
  },
  inject: [
    "selectedInvoiceIds",
    "updateSelectedInvoiceIds",
    "resolvePendingInvoiceRefresh"
  ],
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    },
    qboEnabled: {
      type: Boolean,
      default: false
    },
    totalRows: {
      type: Number,
      default: 0
    }
  },
  emits: ["draw-complete"],
  data() {
    return {
      config: null
    };
  },
  computed: {
    ...mapGetters(["accountingProcessor", "showAccountingClasses"]),
    ...mapState(["tenant", "fetchingTenant"]),
    showClassStatus({ showAccountingClasses, accountingProcessor }) {
      return accountingProcessor === "qbo" && showAccountingClasses;
    }
  },
  watch: {
    tenant(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setConfig();
      }
    }
  },
  mounted() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
    if (this.tenant) {
      this.setConfig();
    }
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    handleMapRowClass(params) {
      if (!this.qboEnabled) {
        return "";
      }

      return params.data.export_status_value === "Failed" ? "failed" : "";
    },
    setConfig() {
      let columns = [
        {
          data: "status",
          title: "Type",
          filterName: "invoice_batch_type",
          orderable: false
        }
      ];

      if (this.qboEnabled) {
        columns = [].concat(columns, [
          {
            data: "invoice_export_status",
            title: "Export Status",
            orderable: false,
            searchable: false
          }
        ]);
      }

      columns = [].concat(columns, [
        { data: "date", title: "Invoice Date", searchable: false },
        { data: "invoice" },
        { data: "job" },
        { data: "location_name", title: "Location Name", orderable: false },
        {
          data: "service_address",
          title: "Service Address",
          orderable: false
        },
        { data: "customer" },
        { data: "customer_email", title: "Email" },
        { data: "customer_phone", title: "Phone" },
        { data: "address", title: "Billing Address", orderable: false },
        { data: "department", searchable: false },
        {
          data: "accounting_class_status",
          title: "Classes",
          orderable: false,
          visible: this.showClassStatus
        },
        { data: "total", className: "text-right", searchable: false },
        { data: "balance", className: "text-right", searchable: false }
      ]);

      const sortColumnName = "date";

      let sortColumn = 1;
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].data === sortColumnName) {
          sortColumn = i + 1;
          break;
        }
      }

      this.config = {
        order: [[sortColumn, "desc"]],
        columns,
        vueComponents: [
          {
            rootId: "export-status-vue",
            component: UnbatchedExportStatus,
            options: {
              store
            }
          },
          {
            rootId: "accounting-class-status-vue",
            component: AccountingClassStatus,
            options: {
              store
            }
          }
        ]
      };
    },
    handleUpdateBulkSelectIds(ids) {
      this.updateSelectedInvoiceIds(ids);
    },
    refreshDataAndSelectAll() {
      const childDatatable = this.$refs.datatable.$refs.datatable;
      const exportReport = this;
      childDatatable.refreshDataWithCallback(function () {
        exportReport.resolvePendingInvoiceRefresh();
        childDatatable.markAllSelectionsAcrossPages();
      });
    },
    selectAll() {
      const childDatatable = this.$refs.datatable.$refs.datatable;
      this.resolvePendingInvoiceRefresh();
      childDatatable.markAllSelectionsAcrossPages();
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(tr.failed.even:nth-child(2n)) {
  background-color: var(--sera-color-accents-red-100) !important;
}

:deep(tr.failed.odd) {
  background-color: var(--sera-color-accents-red-100) !important;
}

:deep(tr.failed.even:nth-child(2n)) {
  background-color: var(--sera-color-accents-red-100) !important;
  &.dt-selected-row {
    background-color: var(--sera-color-accents-red-200) !important;

    .status-badge {
      background-color: var(--sera-color-accents-red-300) !important;
    }
  }
}

:deep(tr.failed.odd) {
  background-color: var(--sera-color-accents-red-100) !important;
  &.dt-selected-row {
    background-color: var(--sera-color-accents-red-200) !important;

    .status-badge {
      background-color: var(--sera-color-accents-red-300) !important;
    }
  }
}
</style>
